import { QUANT_PATH_SEPARATOR, pathStartsWith } from '@trinity/chat/utils/quant-filter';

export type FilterTreeNode = {
  id: string;
  label: string;
  children: FilterTreeNode[];
};

/**
 * Joins a path array into a string ID.
 * e.g. ["Segments", "Informed Believers"] → "Segments > Informed Believers"
 */
export function pathToId(path: string[]): string {
  return path.join(QUANT_PATH_SEPARATOR);
}

/**
 * Splits a string ID back into a path array.
 * e.g. "Segments > Informed Believers" → ["Segments", "Informed Believers"]
 */
export function idToPath(id: string): string[] {
  return id.split(QUANT_PATH_SEPARATOR);
}

/**
 * Deep equality check for two path arrays.
 */
export function pathEquals(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

/**
 * Given all available leaf paths and a selected node's path,
 * returns all leaf paths that start with the selected path.
 */
export function getLeafPaths(allPaths: string[][], selectedPath: string[]): string[][] {
  return allPaths.filter((p) => pathStartsWith(p, selectedPath));
}

/**
 * Checks whether ALL leaf paths under `parentPath` are selected.
 */
export function allLeavesSelected(
  allPaths: string[][],
  selectedPaths: string[][],
  parentPath: string[]
): boolean {
  const leaves = getLeafPaths(allPaths, parentPath);
  if (leaves.length === 0) return false;
  return leaves.every((leaf) => selectedPaths.some((sp) => pathEquals(sp, leaf)));
}

/**
 * Checks whether SOME (but not all) leaf paths under `parentPath` are selected.
 */
export function someLeavesSelected(
  allPaths: string[][],
  selectedPaths: string[][],
  parentPath: string[]
): boolean {
  const leaves = getLeafPaths(allPaths, parentPath);
  if (leaves.length === 0) return false;
  const selectedCount = leaves.filter((leaf) => selectedPaths.some((sp) => pathEquals(sp, leaf))).length;
  return selectedCount > 0 && selectedCount < leaves.length;
}

/**
 * Builds a recursive tree structure from flat path arrays.
 *
 * Input: [["Segments", "Informed Believers"], ["Segments", "Precision Splitters"], ["Practice Setting", "Academic"]]
 * Output: [
 *   { id: "Segments", label: "Segments", children: [
 *     { id: "Segments > Informed Believers", label: "Informed Believers", children: [] },
 *     { id: "Segments > Precision Splitters", label: "Precision Splitters", children: [] },
 *   ]},
 *   { id: "Practice Setting", label: "Practice Setting", children: [
 *     { id: "Practice Setting > Academic", label: "Academic", children: [] },
 *   ]},
 * ]
 */
export function buildFilterTree(paths: string[][], parentPath: string[] = []): FilterTreeNode[] {
  const groups = new Map<string, string[][]>();

  for (const path of paths) {
    if (path.length === 0) continue;
    const head = path[0];
    if (!groups.has(head)) {
      groups.set(head, []);
    }
    if (path.length > 1) {
      groups.get(head).push(path.slice(1));
    }
  }

  const nodes: FilterTreeNode[] = [];

  for (const [label, subPaths] of groups) {
    const nodePath = [...parentPath, label];
    nodes.push({
      id: pathToId(nodePath),
      label,
      children: buildFilterTree(subPaths, nodePath),
    });
  }

  return nodes;
}

/**
 * Removes a path and all its descendants from selectedPaths.
 */
export function removePathAndDescendants(selectedPaths: string[][], pathToRemove: string[]): string[][] {
  return selectedPaths.filter((sp) => !pathStartsWith(sp, pathToRemove));
}

/**
 * Adds all leaf paths under a node to the selection (deduplicating).
 */
export function addLeafPaths(
  currentSelected: string[][],
  allPaths: string[][],
  nodePath: string[]
): string[][] {
  const leavesToAdd = getLeafPaths(allPaths, nodePath);
  const result = [...currentSelected];
  for (const leaf of leavesToAdd) {
    if (!result.some((existing) => pathEquals(existing, leaf))) {
      result.push(leaf);
    }
  }
  return result;
}

/**
 * Collects all distinct names from a set of paths, ordered BFS-style:
 * depth-0 names first, then depth-1, then depth-2, etc.
 * Names that appear at multiple depths are only included once (at the shallowest depth).
 *
 * e.g. [["Product A", "User"], ["Product B", "Non-User"], ["Product A", "Non-User"]]
 * → ["Product A", "Product B", "User", "Non-User"]
 */
export function getDistinctNamesBFS(paths: string[][]): string[] {
  const byDepth = new Map<number, Set<string>>();
  for (const path of paths) {
    for (let depth = 0; depth < path.length; depth++) {
      const name = path[depth];
      if (name == null) continue;
      if (!byDepth.has(depth)) byDepth.set(depth, new Set());
      byDepth.get(depth).add(name);
    }
  }
  const seen = new Set<string>();
  const result: string[] = [];
  for (const names of byDepth.values()) {
    for (const name of names) {
      if (!seen.has(name)) {
        seen.add(name);
        result.push(name);
      }
    }
  }
  return result;
}

/**
 * Given the current selection and all available paths, compute the
 * indeterminate item IDs for the MUI TreeView. A parent node is indeterminate
 * when SOME (but not all) leaf paths under it are selected.
 */
export function getIndeterminateItemIds(
  tree: FilterTreeNode[],
  allPaths: string[][],
  selectedPaths: string[][]
): string[] {
  const result: string[] = [];

  function walk(nodes: FilterTreeNode[]): void {
    for (const node of nodes) {
      if (node.children.length > 0) {
        if (someLeavesSelected(allPaths, selectedPaths, idToPath(node.id))) {
          result.push(node.id);
        }
        walk(node.children);
      }
    }
  }

  walk(tree);
  return result;
}

/**
 * Given the current selection and all available paths, compute the
 * selected item IDs for the MUI TreeView. This checks every node in the tree
 * and returns IDs of nodes where ALL leaves under them are selected.
 */
export function getSelectedItemIds(
  tree: FilterTreeNode[],
  allPaths: string[][],
  selectedPaths: string[][]
): string[] {
  const result: string[] = [];

  function walk(nodes: FilterTreeNode[]): void {
    for (const node of nodes) {
      const nodePath = idToPath(node.id);
      if (node.children.length === 0) {
        // Leaf node: check if it's in selectedPaths
        if (selectedPaths.some((sp) => pathEquals(sp, nodePath))) {
          result.push(node.id);
        }
      } else {
        // Parent node: check if all leaves under it are selected
        if (allLeavesSelected(allPaths, selectedPaths, nodePath)) {
          result.push(node.id);
        }
        walk(node.children);
      }
    }
  }

  walk(tree);
  return result;
}
